// src/pages/Wall.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import QRious from 'qrious';
import bg from '../assets/soul_back.png';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || window.location.origin;

export default function Wall() {
  const wallRef   = useRef(null);
  const qrSendRef = useRef(null);
  const [clearing, setClearing] = useState(false);
  const [toast, setToast] = useState('');

  const socket = useMemo(() => io(SOCKET_URL, {
   path: '/socket.io',
   reconnection: true,
   withCredentials: true
 }), []);

  // 共用：把一段文字生成一顆會彈跳的泡泡
  const spawn = (txt) => {
    const wall = wallRef.current;
    if (!wall) return;
    const el = document.createElement('div');
    el.className = 'msg';
    el.textContent = txt;

    // 隨機色
    const hue = Math.floor(Math.random() * 360);
    el.style.background = `hsl(${hue}, 70%, 45%)`;

    // 初始位置
    wall.appendChild(el);
    const iw = window.innerWidth;
    const ih = window.innerHeight;
    let x = Math.random() * Math.max(1, (iw - el.offsetWidth));
    let y = Math.random() * Math.max(1, (ih - el.offsetHeight));
    let dx = (Math.random() * 4 - 2) || 1;
    let dy = (Math.random() * 4 - 2) || 1;
    el.style.left = x + 'px';
    el.style.top  = y + 'px';

    const t = setInterval(() => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      x += dx; y += dy;
      if (x <= 0 || x >= w - el.offsetWidth)  dx *= -1;
      if (y <= 0 || y >= h - el.offsetHeight) dy *= -1;
      el.style.left = x + 'px';
      el.style.top  = y + 'px';
    }, 10);

    // 90 秒後淡出
    const killer = setTimeout(() => {
      el.style.transition = 'opacity 1s ease';
      el.style.opacity = '0';
      setTimeout(() => { clearInterval(t); el.remove(); }, 1000);
    }, 90 * 1000);

    // 回收（避免記憶體累積）
    return () => { clearInterval(t); clearTimeout(killer); el.remove(); };
  };

  // 產生左上角 QR
  useEffect(() => {
    new QRious({
      element: qrSendRef.current,
      value: `${window.location.origin}/send`,
      size: 120,
      background: '#ffffff',
      foreground: '#4f46e5',
    });
  }, []);

  // A) 進頁面先把 DB 最近 100 筆載入（由舊到新顯示）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/wall/recent');
        const rows = await r.json();             // [{ID, CONTENT, CREATED_AT}, ...]
        if (cancelled) return;
        // 由舊到新
        rows.reverse().forEach(row => spawn(row.CONTENT));
      } catch (e) {
        console.error('載入歷史留言失敗', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // B) 即時 socket：有人送就冒泡
  useEffect(() => {
    const onMsg = (txt) => spawn(String(txt || ''));
    socket.on('connect', () => console.log('[wall] socket connected'));
    socket.on('msg', onMsg);
    return () => {
      socket.off('msg', onMsg);
      socket.close();
    };
  }, [socket]);

  // 清除（後端 + 畫面）
  const clearMessages = async () => {
    if (clearing) return;
    setClearing(true);
    try {
      await fetch('/api/wall/clear', { method: 'POST' });
      if (wallRef.current) wallRef.current.innerHTML = '';
      setToast('已清空留言');
    } catch (e) {
      console.error('清除失敗', e);
      setToast('清除失敗');
    } finally {
      setTimeout(() => setToast(''), 1400);
      setClearing(false);
    }
  };

  return (
    <div style={styles.root(bg)}>
      <div id="wall" ref={wallRef} style={styles.wall} />

      <div style={styles.hint}>掃我留下有趣留言～</div>
      <canvas ref={qrSendRef} style={styles.qrLeft} />

      <button onClick={clearMessages} disabled={clearing} style={{...styles.clearBtn, opacity: clearing ? .7 : 1}}>
        {clearing ? '清除中…' : '清除留言'}
      </button>

      {toast && <div style={styles.toast}>{toast}</div>}

      <style>{cssMsg}</style>
    </div>
  );
}

const styles = {
  root: (bg) => ({
    position: 'relative',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
    backgroundColor: '#000',
    backgroundImage: `url(${bg})`,
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
    fontFamily: 'system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial',
  }),
  wall: { position: 'absolute', inset: 0, zIndex: 1 },
  hint: {
    position: 'fixed', left: '2rem', top: 'calc(2rem + 120px + 1.5rem)',
    color: '#fff', fontSize: '1rem', fontWeight: 700,
    textShadow: '0 0 5px rgba(0,0,0,.7)', zIndex: 2,
  },
  qrLeft: {
    position: 'fixed', left: '2rem', top: '2rem',
    border: '8px solid #fff', borderRadius: '8px', zIndex: 2,
  },
  clearBtn: {
    position: 'fixed', right: '2rem', top: '2rem',
    padding: '0.5rem 1rem',
    fontSize: '1rem',
    fontWeight: 700,
    background: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    zIndex: 3,
  },
  toast: {
    position:'fixed',
    left:'50%',
    transform:'translateX(-50%)',
    bottom:24,
    background:'rgba(0,0,0,.8)',
    color:'#fff',
    padding:'8px 14px',
    borderRadius:12,
    zIndex: 4,
    fontSize: 14,
    fontWeight: 700
  }
};

const cssMsg = `
.msg{
  position:absolute;
  padding:.5rem 1rem;
  border-radius:1.7rem;
  max-width:28rem;
  white-space:pre-wrap;
  word-break:break-word;
  font-size:1.7rem;
  font-weight:700;
  color:#fff;
  user-select:none;
  box-shadow:0 6px 14px rgba(0,0,0,.25);
}
`;
